import { useState } from "react";
import { Link } from "react-router-dom";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { RotateCcw, Shield, UserMinus } from "lucide-react";

import { Avatar } from "../../components/Avatar/Avatar";
import { Button } from "../../components/Button/Button";
import { Modal } from "../../components/Modal/Modal";
import { Skeleton } from "../../components/Skeleton/Skeleton";
import { useOptionalToast } from "../../components/Toast/toastContext";
import { ApiError, describeError } from "../../lib/errors";
import { listBlocks, unblockPlayer } from "./api";
import { formatRelativeLong } from "./relativeTime";
import type { GuildBlock, GuildDetail } from "./types";
import { visibilityLabel } from "./visibility";
import { blocksQueryKey } from "./queryKeys";
import styles from "./GuildBlockedTable.module.css";

interface GuildBlockedTableProps {
  projectId: string;
  guildId: string;
  /** The loaded guild — supplies `name` and `visibility` for the modal copy. */
  guild: GuildDetail;
  /** Called after a successful unblock, so the parent can refresh the feed. */
  onChanged: () => void;
}

/**
 * Rows per request. The section is paged rather than capped because Unblock
 * exists for the oldest blocks as much as the newest — a guild that has been
 * kicking people for two years must not put the one regretted decision out of
 * the panel's reach.
 */
const PAGE_SIZE = 25;

/**
 * The Blocked section of `GuildDetailPage` — ports screen 1 of
 * docs/mockups/guild-blocked-players.html, and sits between Members and Chat
 * because that is the table a blocked player arrives from.
 *
 * It lists who this guild kicked, when, and who did it, and offers the one
 * action there is: lifting the block. That is support's undo of a permanent
 * decision, so it goes through a confirm modal — but it is not destructive,
 * and the confirm button is the primary one rather than the danger one.
 */
export function GuildBlockedTable({
  projectId,
  guildId,
  guild,
  onChanged,
}: GuildBlockedTableProps) {
  const toast = useOptionalToast();
  const qc = useQueryClient();
  const [pending, setPending] = useState<GuildBlock | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);

  const query = useInfiniteQuery({
    queryKey: blocksQueryKey(projectId, guildId),
    queryFn: ({ pageParam }) =>
      listBlocks(projectId, guildId, { page: pageParam, limit: PAGE_SIZE }),
    initialPageParam: 1,
    // The total comes back with every page, so "is there more" is answered by
    // what has been loaded against what exists — no separate count to keep in
    // step, and a kick landing between two pages cannot strand the button.
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.items.length, 0);
      return loaded < last.total ? pages.length + 1 : undefined;
    },
    enabled: !!projectId && !!guildId,
  });

  /** Put the section back in step with the server and tell the parent. */
  function refresh() {
    setPending(null);
    setModalError(null);
    void qc.invalidateQueries({ queryKey: blocksQueryKey(projectId, guildId) });
    onChanged();
  }

  const unblockMutation = useMutation({
    mutationFn: (b: GuildBlock) => unblockPlayer(projectId, guildId, b.player_id),
    onSuccess: () => {
      refresh();
      toast?.push({ variant: "success", message: "Player unblocked" });
    },
    onError: (err) => {
      // 404 means the block is already gone — a second operator on the same
      // ticket, or a deleted player. The server is in the state this dialog
      // was asking for, so the only thing out of date is the screen: close it
      // and re-read, rather than leaving an error under a button whose one
      // effect is to produce the same error again.
      if (err instanceof ApiError && err.status === 404) {
        refresh();
        toast?.push({
          variant: "info",
          message: "That block had already been lifted",
        });
        return;
      }
      setModalError(
        err instanceof ApiError
          ? describeError(err.code)
          : describeError("internal"),
      );
    },
  });

  function closeModal() {
    if (unblockMutation.isPending) return;
    setPending(null);
    setModalError(null);
  }

  const items = query.data?.pages.flatMap((p) => p.items) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>Blocked</h3>
        <span className={styles.cardSub}>
          {subtitle(query.isLoading, query.isError, total)}
        </span>
      </div>

      {query.isLoading ? (
        <LoadingRows />
      ) : query.isError ? (
        <div className={styles.emptyState}>
          <div className={styles.ei}>
            <UserMinus size={26} aria-hidden="true" />
          </div>
          <h3>Couldn&apos;t load the blocked players</h3>
          <p>
            {query.error instanceof ApiError
              ? describeError(query.error.code)
              : describeError("internal")}
          </p>
          <button
            type="button"
            className={styles.retryLink}
            onClick={() => void query.refetch()}
          >
            Retry
          </button>
        </div>
      ) : items.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.ei}>
            <UserMinus size={26} aria-hidden="true" />
          </div>
          <h3>Nobody is blocked</h3>
          <p>
            Kicked players land here and stay until somebody unblocks them or
            the guild invites them back.
          </p>
        </div>
      ) : (
        <>
          <table className={styles.table}>
            <thead>
              <tr>
                <th style={{ width: "50%" }}>Player</th>
                <th>Blocked</th>
                <th>Kicked by</th>
                <th aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.player_id}>
                  <td>
                    <div className={styles.memberCell}>
                      <Avatar name={b.display_name} size={30} />
                      <Link
                        to={`/projects/${projectId}/players/${b.player_id}`}
                        className={styles.link}
                      >
                        {b.display_name}
                      </Link>
                    </div>
                  </td>
                  <td className={styles.muted}>
                    {formatRelativeLong(b.created_at)}
                  </td>
                  <td>
                    <KickedBy block={b} projectId={projectId} />
                  </td>
                  <td className={styles.act}>
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<RotateCcw size={13} aria-hidden="true" />}
                      onClick={() => {
                        setModalError(null);
                        setPending(b);
                      }}
                    >
                      Unblock
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {query.hasNextPage && (
            <div className={styles.more}>
              <button
                type="button"
                className={styles.moreLink}
                disabled={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                {query.isFetchingNextPage
                  ? "Loading…"
                  : `Show older blocks (${total - items.length} more)`}
              </button>
            </div>
          )}
        </>
      )}

      {pending && (
        <UnblockModal
          block={pending}
          guild={guild}
          error={modalError}
          busy={unblockMutation.isPending}
          onClose={closeModal}
          onConfirm={() => unblockMutation.mutate(pending)}
        />
      )}
    </div>
  );
}

/**
 * "3 blocked · …" / "nobody blocked" — the mockup's card subtitle.
 *
 * A failed load gets its own answer rather than falling through to the zero.
 * `total` is 0 when there is no data at all, and printing "nobody blocked"
 * over the words "couldn't load" states something about the guild that the
 * request never learned — on a guild that may well have thirty.
 */
function subtitle(loading: boolean, failed: boolean, total: number): string {
  if (loading) return "loading…";
  if (failed) return "couldn't load";
  if (total === 0) return "nobody blocked";
  return `${total} blocked · kicked players can't return without an invite`;
}

/**
 * Who did the kicking: a player, linked to their card — or the Moderator tag,
 * for a kick ordered from this panel. The tag is also what a block whose
 * author has since been deleted falls back to: the guild's decision outlives
 * the account that made it, and there is no name left to show.
 */
function KickedBy({
  block,
  projectId,
}: {
  block: GuildBlock;
  projectId: string;
}) {
  if (block.blocked_by && block.blocked_by_name) {
    return (
      <Link
        to={`/projects/${projectId}/players/${block.blocked_by}`}
        className={styles.link}
      >
        {block.blocked_by_name}
      </Link>
    );
  }
  return (
    <span className={styles.tag}>
      <Shield size={11} aria-hidden="true" />
      Moderator
    </span>
  );
}

/** Unblock confirm modal — mockup screen 3. */
function UnblockModal({
  block,
  guild,
  error,
  busy,
  onClose,
  onConfirm,
}: {
  block: GuildBlock;
  guild: GuildDetail;
  error: string | null;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal
      open
      onClose={onClose}
      title={`Unblock ${block.display_name}?`}
      closeDisabled={busy}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            disabled={busy}
            loading={busy}
          >
            Unblock
          </Button>
        </>
      }
    >
      <p className={styles.modalBody}>
        <b>{block.display_name}</b> will be able to join <b>{guild.name}</b>{" "}
        again — the guild is <b>{visibilityLabel(guild.visibility)}</b>, so{" "}
        {wayBackIn(guild.visibility)} Kicking them once more re-creates the
        block.
      </p>
      {error && (
        <p className={styles.modalBody} role="alert" style={{ marginTop: 10 }}>
          {error}
        </p>
      )}
    </Modal>
  );
}

/** What lifting the block actually opens, per join mode. */
function wayBackIn(visibility: string): string {
  switch (visibility) {
    case "open":
      return "they can walk straight back in.";
    case "closed":
      return "they can apply again.";
    default:
      return "they still need an invitation.";
  }
}

/** Three placeholder rows while the list loads. */
function LoadingRows() {
  return (
    <div className={styles.loading}>
      {[0, 1, 2].map((i) => (
        <div className={styles.loadingRow} key={i}>
          <Skeleton width={30} height={30} radius={999} />
          <Skeleton width={140} height={13} />
          <Skeleton width={70} height={13} />
        </div>
      ))}
    </div>
  );
}
